import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';

interface AudioMessagePlayerProps {
  audioUrl: string;
  durationMs?: number;
  isFromAI?: boolean;
  timestamp: string;
}

export const AudioMessagePlayer: React.FC<AudioMessagePlayerProps> = ({
  audioUrl,
  durationMs,
  isFromAI,
  timestamp,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [waveformData] = useState(() => 
    Array.from({ length: 30 }, () => Math.random() * 100)
  );

  const formatTime = (ms: number) => {
    const totalSec = Math.floor(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const handleTogglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime * 1000);
    }
  };

  const handleLoadedMetadata = () => {
    // metadata loaded
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (audioRef.current) {
      const time = (parseFloat(e.target.value) / 100) * (audioRef.current.duration || 0);
      audioRef.current.currentTime = time;
      setCurrentTime(time * 1000);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  const progress = durationMs ? (currentTime / durationMs) * 100 : 0;

  return (
    <div className={`flex items-center gap-2 py-1 px-0.5 min-w-[180px] max-w-full ${isFromAI ? 'text-gray-800 dark:text-gray-200' : 'text-white'}`}>
      <audio 
        ref={audioRef} 
        src={audioUrl} 
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
      />
      
      {/* Play Button */}
      <button
        onClick={handleTogglePlay}
        className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full transition-all active:scale-95 ${
          isFromAI 
            ? 'hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300' 
            : 'hover:bg-white/10 text-white'
        }`}
      >
        {isPlaying ? (
          <Pause className="w-5 h-5 fill-current" />
        ) : (
          <Play className="w-5 h-5 fill-current ml-0.5" />
        )}
      </button>

      {/* Waveform and Progress */}
      <div className="flex-1 flex flex-col justify-center min-w-0">
        <div className="h-8 flex items-center">
          <div className="relative flex-1 h-5 flex items-center gap-[1.5px] group">
            {/* Waveform Bars */}
            <div className="absolute inset-0 flex items-center gap-[1.5px] pointer-events-none">
              {waveformData.map((height, i) => {
                const isPlayed = (i / waveformData.length) * 100 < progress;
                return (
                  <div
                    key={i}
                    className="w-[2px] rounded-full transition-colors duration-150"
                    style={{
                      height: `${40 + (height * 0.5)}%`,
                      backgroundColor: isPlayed 
                        ? (isFromAI ? '#3b82f6' : '#fff') 
                        : (isFromAI ? '#cbd5e1' : 'rgba(255,255,255,0.3)')
                    }}
                  />
                );
              })}
            </div>
            
            {/* Hidden range input for seeking */}
            <input
              type="range"
              min="0"
              max="100"
              value={progress}
              onChange={handleSeek}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            />
          </div>
        </div>
        
        <div className="flex justify-between items-center text-[11px] font-medium opacity-90 px-0.5 -mt-1.5">
          <span>{isPlaying ? formatTime(currentTime) : (durationMs ? formatTime(durationMs) : '0:00')}</span>
          <span className="opacity-70">{timestamp}</span>
        </div>
      </div>
    </div>
  );
};
